import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes } from "lucide-react";

/**
 * After a meal is logged, ask backend which pantry items it consumed.
 * Show a confirm dialog with editable quantities → call /inventory/deduct.
 * `trigger` is a `{food_name, serving}` object — set non-null to open.
 */
export default function DeductionDialog({ trigger, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/inventory/deduction-suggest", {
          food_name: trigger.food_name,
          serving: trigger.serving || "",
        });
        setItems(data.items || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [trigger]);

  const apply = async () => {
    const toDeduct = items.filter((it) => it.in_pantry && it.quantity > 0);
    if (toDeduct.length === 0) {
      onClose?.();
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/inventory/deduct", {
        items: toDeduct.map((i) => ({
          name: i.matched_name || i.name,
          quantity: Number(i.quantity) || 0,
          unit: i.unit || "",
          inventory_id: i.inventory_id,
        })),
      });
      toast.success(`Pantry updated — ${data.applied.length} item${data.applied.length === 1 ? "" : "s"} deducted`);
    } catch (e) {
      toast.error("Couldn't update pantry");
    } finally {
      setApplying(false);
      onClose?.();
    }
  };

  const update = (idx, key, val) => {
    setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  };
  const toggle = (idx) => update(idx, "in_pantry", !items[idx].in_pantry);

  const open = !!trigger;
  const pantryItems = items.filter((i) => i.in_pantry);
  const otherItems = items.filter((i) => !i.in_pantry);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="rounded-3xl max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Boxes size={20} className="text-secondary" /> Deduct from pantry?
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="py-6 text-muted-foreground text-sm">Estimating ingredients…</div>}
        {!loading && items.length === 0 && (
          <div className="py-6 text-muted-foreground text-sm">No matching pantry items found.</div>
        )}
        {!loading && pantryItems.length > 0 && (
          <div className="space-y-2" data-testid="deduction-list">
            <div className="text-overline">in your pantry</div>
            {items.map((it, i) => !it.in_pantry ? null : (
              <div key={i} className="flex items-center gap-2 bg-surface rounded-xl p-2" data-testid={`deduct-row-${i}`}>
                <input type="checkbox" checked={it.in_pantry} onChange={() => toggle(i)} data-testid={`deduct-check-${i}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{it.matched_name || it.name}</div>
                  {it.matched_name && it.matched_name.toLowerCase() !== (it.name || "").toLowerCase() && (
                    <div className="text-xs text-muted-foreground">from: "{it.name}"</div>
                  )}
                </div>
                <Input
                  type="number"
                  value={it.quantity}
                  onChange={(e) => update(i, "quantity", Number(e.target.value))}
                  className="w-20 h-8 text-right"
                  data-testid={`deduct-qty-${i}`}
                />
                <span className="text-xs text-muted-foreground w-10">{it.unit}</span>
              </div>
            ))}
          </div>
        )}
        {!loading && otherItems.length > 0 && (
          <div className="space-y-1 mt-2">
            <div className="text-overline">not in pantry (skipped)</div>
            {otherItems.map((it, i) => (
              <div key={i} className="text-xs text-muted-foreground">• {it.name} ({it.quantity} {it.unit})</div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onClose?.()} className="rounded-full" data-testid="deduct-skip-btn">Skip</Button>
          <Button onClick={apply} disabled={applying || pantryItems.length === 0} className="rounded-full bg-primary" data-testid="deduct-apply-btn">
            {applying ? "Updating…" : "Confirm & deduct"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
